import React, { useEffect, useMemo, useState } from "react";
import { Monitor, TriangleAlert } from "lucide-react";
import * as signalR from "@microsoft/signalr";
import { API_BASE_URL } from "../config";
import { useAuth } from "../auth";
import { useTheme } from "../components/theme-provider";


const styles = `

  .last-update {
    font-size: 0.85em;
    margin-top: 10px;
  }

  .error-message {
    font-size: 0.85em;
  }

  .no-data {
    text-align: center;
    padding: 50px;
    color: #999;
    font-size: 1.2em;
  }
`;



type ServiceStatusInfo = {

  serviceName: string;
  status: string;
  displayName: string;
  timestamp: Date;
  probeServiceName: string;
  machineName: string;
  errorMessage: string;


};

type ProbeInfo = {

  
  ProbeServiceName: string;
  Services: ServiceStatusInfo[];
  MachineName: string;


};

const NO_SIGNAL_THRESHOLD_MS = 6 * 60 * 1000;
const NO_SIGNAL_CHECK_INTERVAL_MS = 30 * 1000;

function getServiceKey(item: ServiceStatusInfo) {
  return `${item.probeServiceName}::${item.serviceName}`;
}

function mergeStatusList(
  current: ServiceStatusInfo[],
  incoming: ServiceStatusInfo[]
) {
  const merged = new Map<string, ServiceStatusInfo>();

  current.forEach((item) => {
    merged.set(getServiceKey(item), item);
  });

  incoming.forEach((item) => {
    merged.set(getServiceKey(item), item);
  });

  return Array.from(merged.values());
}

function getStatusClass(status :string) {
  const value = String(status || "").toLowerCase();

  if (value === "running") return "status-running border-l-teal-400 dark:border-l-teal-600 bg-teal-50 dark:bg-teal-600/15";
  if (value === "stopped") return "status-stopped border-l-pink-600 dark:border-l-pink-700 bg-pink-50 dark:bg-pink-700/20";

  return "status-error border-l-orange-500 dark:border-l-orange-300 bg-orange-50 dark:bg-orange-700/15";
}

function getStatusBadgeClass(status:string) {
  const value = String(status || "").toLowerCase();

  if (value === "running") return "status-badge-running bg-teal-400 dark:bg-teal-600 text-white";
  if (value === "stopped") return "status-badge-stopped bg-pink-600 dark:bg-pink-700 text-white";

  return "status-badge-error bg-orange-600 dark:bg-orange-300 text-white dark:text-zinc-900";
}

function formatDate(value : Date) {
  if (!value) return "Unbekannt";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unbekannt";
  }

  return date.toLocaleString("de-DE");
}

function ServiceItem({ service }: { service: ServiceStatusInfo }) {
  const status = service.status || "Unknown";
  const displayName =
    service.displayName || service.serviceName || "Unbekannter Dienst";
  const errorMessage = service.errorMessage;

  return (
    <div className={`service-item bg-gray-100 rounded-xl p-4 border-l-4 ${getStatusClass(status)}`}>
      <span className="service-name text-semibold">{displayName}</span>
      <span className={`service-status float-right rounded-3xl px-4 py-1 text-sm ${getStatusBadgeClass(status)}`}>
        {status}
      </span>
      <div style={{ clear: "both" }} />
      {errorMessage ? (
        <div className="error-message flex items-center gap-1 text-orange-600 dark:text-orange-300"><TriangleAlert className="w-5 h-5" /> {errorMessage}</div>
      ) : null}
        <div className="last-update">
          Letzte Aktualisierung: {formatDate(service.timestamp)}
        </div>
    </div>
  );
}

function ProbeCard({ probe }: { probe: ProbeInfo }) {
  return (
    <div className="col-xl-4 col-lg-6 col-md-12">
      <div className="probe-card mt-0 mb-8 overflow-x-auto my-0 rounded-xl shadow-xl bg-white dark:bg-zinc-700 min-h-[200px] p-4">
        <div className="probe-header">
          <div className="probe-name text-2xl pr-4">{probe.ProbeServiceName}</div>
          <div className="flex items-center gap-1">
            <Monitor className="w-5 h-5 mr-2" /> <span className="machine-name font-light">{probe.MachineName}</span>
          </div>
        </div>

        <div className="services-list grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
          {probe.Services.map((service: ServiceStatusInfo, index: number) => (
            <ServiceItem
              key={`${service.serviceName}-${index}`}
              service={service}
            />
          ))}

        </div>

      
      </div>
    </div>
  );
}

export function MonitorPage() {
  const [statusList, setStatusList] = useState<ServiceStatusInfo[]>([]);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    text: "✗ Getrennt",
  });
  const { accessToken } = useAuth();
  const { theme } = useTheme();
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    const resolveTheme = () => {
      const nextTheme = theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme;

      setResolvedTheme(nextTheme);
    };

    resolveTheme();

    if (theme !== "system") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => resolveTheme();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [theme]);

  //const iframeUrl = `https://go.spotlight-mobil.de/freespace.php?state=framed&style=${resolvedTheme}`;

     const markStaleAsNoSignal = () => {
      const now = Date.now();

      setStatusList((current) => {
        let changed = false;

        const updated = current.map((item) => {
          const timestampValue = new Date(item.timestamp as unknown as string).getTime();
          if (Number.isNaN(timestampValue)) {
            return item;
          }

          const isStale = now - timestampValue > NO_SIGNAL_THRESHOLD_MS;
          if (!isStale || item.status === "No Signal") {
            return item;
          }

          changed = true;
          return { ...item, status: "No Signal" };
        });

        return changed ? updated : current;
      });
    };

  useEffect(() => {
    if (!connectionStatus.connected) {
      return;
    }

 

    // Run once immediately after connection and then periodically.
    markStaleAsNoSignal();
    const timerId = window.setInterval(markStaleAsNoSignal, NO_SIGNAL_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(timerId);
    };
  }, [connectionStatus.connected]);

  useEffect(() => {
    let isMounted = true;
    let retryTimerId: ReturnType<typeof window.setTimeout> | undefined;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/ServiceMonitorHub`, {
        accessTokenFactory: () => accessToken || "",
      })
      .withAutomaticReconnect()
      .build();

    const setConnected = (connected: boolean) => {
      if (!isMounted) return;

      setConnectionStatus({
        connected,
        text: connected ? "✓ Verbunden" : "✗ Getrennt",
      });
    };

    connection.on("connected", () => {
      setConnected(true);
      console.log("Verbindung zum SignalR Hub hergestellt");
    });

    connection.on("UpdateServiceStatus", (newStatusList) => {
      console.log("Service-Status empfangen:", newStatusList);
      setStatusList((current) =>
        mergeStatusList(current, Array.isArray(newStatusList) ? newStatusList : [])
      );
      markStaleAsNoSignal();
    });

    connection.onclose(() => {
      setConnected(false);
      console.log("Verbindung zum SignalR Hub getrennt");
    });

    connection.onreconnecting(() => {
      setConnected(false);
      console.log("Versuche Wiederverbindung...");
    });

    connection.onreconnected(() => {
      setConnected(true);
      console.log("Wiederverbindung erfolgreich");
    });

    const startConnection = async () => {
      try {
        await connection.start();
        setConnected(true);
        var lastResults = await connection.invoke("JoinMonitorGroup");
        if (lastResults) {
          console.log("Letzter Status vom Server:", lastResults);
          setStatusList((current) =>
            mergeStatusList(current, Array.isArray(lastResults) ? lastResults : [])
          );
          markStaleAsNoSignal();

        }
        console.log("SignalR-Verbindung gestartet");
      } catch (error) {
        setConnected(false);
        console.error("Fehler beim Verbinden:", error);

        retryTimerId = window.setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      isMounted = false;
      window.clearTimeout(retryTimerId);

      connection.off("connected");
      connection.off("UpdateServiceStatus");
      connection.stop().catch(() => {
        // Ignore shutdown errors during unmount.
      });
    };
  }, []);

  const groupedProbes = useMemo(() => {
    const grouped = new Map<string, ProbeInfo>();

    statusList.forEach((item) => {
      const probeName = item.probeServiceName;

      if (!grouped.has(probeName)) {
        grouped.set(probeName, {
          ProbeServiceName: probeName,
          MachineName: item.machineName || "Unbekannt",
          Services: [] as ServiceStatusInfo[]
        });
      }

      const probe = grouped.get(probeName);
      if (probe) {
        probe.Services.push(item);

       
      }
    });

    return Array.from(grouped.values());
  }, [statusList]);

  return (
      <div className="service-monitor-page h-[calc(100vh-60px)] flex flex-col">

        <div className="content-header flex justify-start pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
          <h1 className="font-bold text-2xl">Server Monitoring</h1>
          <span
            className={`connection-status text-white rounded-3xl ml-10 px-4 flex items-center ${connectionStatus.connected ? "connected bg-teal-400 dark:bg-teal-600" : "disconnected bg-red-400"
              }`}
          >
            {connectionStatus.text}
          </span>
        </div>



        <style>{styles}</style>

        <div className="flex-1 overflow-auto px-10">
          <section className="pt-0 pb-6">

            {/* <div className="mt-4 mb-8 rounded-xl shadow-xl bg-white dark:bg-zinc-700 px-0 py-2">
              <iframe
                key={resolvedTheme}
                src={iframeUrl}
                title="Free space"
                className="max-h-[40px] w-full border-0 min-h-[2.8rem]"
                loading="lazy"
              />
            </div> */}

            <div className="container-fluid mt-4">
              <div className="row">
                {groupedProbes.length === 0 ? (
                  <div className="col-12 no-data">
                    Warten auf Daten vom Probe-Service...
                  </div>
                ) : (
                  groupedProbes.map((probe) => (
                    <ProbeCard key={probe.ProbeServiceName} probe={probe} />
                  ))
                )}
              </div>
            </div>
          </section>
        </div>

      </div>
  );
}
